import './index.css'

import { MdCheck as CheckIcon } from '@react-icons/all-files/md/MdCheck'
import cn from 'clsx'
import uniqBy from 'lodash/uniqBy'
import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { Link, NavigateFunction, useNavigate } from 'react-router-dom'

import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { sendBusinessInvite } from '@disco/state-management/actions/BusinessUsersActions'
import { loadGroups } from '@disco/state-management/actions/GroupActions'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import { getIsTrial } from '@disco/state-management/selectors/business'
import {
  getGroups,
  getTerritories,
} from '@disco/state-management/selectors/groups'
import { isDisableInviteTeamMember } from '@disco/state-management/selectors/invites'
import type { UsersLimits } from '@disco/state-management/selectors/user'
import {
  getUsersLimits,
  isBankDraftSubscriber,
} from '@disco/state-management/selectors/user'
import { parseServerError } from '@disco/utils/captureError'
import { getEmailHandleFromAngularBrackets } from '@disco/utils/email'
import { updateItem } from '@disco/utils/immutable'
import { getFirstLastName } from '@disco/utils/string-utils'
import { validateEmail } from '@disco/utils/validation'

import type { HttpErrorType } from 'types/errors'
import type { Group, GroupId } from 'types/group'
import type { User } from 'types/user'

import Row from 'components/common/row'
import UsersQuotaAlert from 'components/common/users-quota-alert'
import NonAdminInviteUserModal from 'components/modals/non-admin-invite-user-modal'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import ContactUsLink from 'components/ui/contact-us-link'
import EllipsisMenu from 'components/ui/ellipsis-menu'
import Heading from 'components/ui/heading'
import InlineLink from 'components/ui/inline-link'
import Label from 'components/ui/label'
import ModalWindow from 'components/ui/modal-window'
import Stack from 'components/ui/stack'
import Text from 'components/ui/text'
import TextField from 'components/ui/text-field'

const mapStateToProps = state => ({
  user: state.user,
  territories: getTerritories(state),
  groups: getGroups(state),
  isPhone: isDeviceTypePhone(state),
  isTrial: getIsTrial(state),
  usersLimits: getUsersLimits(state),
  disableInviteTeamMember: isDisableInviteTeamMember(state),
  bankDraftSubscriber: isBankDraftSubscriber(state),
})

const mapDispatchToProps = {
  sendBusinessInvite,
  sendMixpanelEvent,
  loadGroups,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>
const emptyMember: Member = {
  email: '',
  first_name: '',
  last_name: '',
  errors: null,
}

function isMemberNonEmpty(member: Member): boolean {
  return !!(member.email || member.first_name || member.last_name)
}

function isMemberNotInvited(invited) {
  return (member: Member) => {
    return !invited.find(i => i.email === member.email)
  }
}

type Member = {
  email: string
  first_name: string
  last_name: string
  message?: string
  territory_id?: GroupId
  // TODO: consider separating Member type due to server/client fields
  // client-only Member fields:
  errors: Record<string, boolean> | null | undefined
  // server-only Member fields:
  id?: number
  invitee?: string
  // user url: https://example.disco.ac/api/user/1/
  inviter?: string
  // user url: https://example.disco.ac/api/user/1/
  url?: string // invite url: https://example.disco.ac/api/invite/1/
}
interface AdditionalProps {
  navigate: NavigateFunction
}

type Props = PropsFromRedux & AdditionalProps
type State = {
  members: Array<Member>
  message: string
  invited: Array<Member>
  failed: Array<{
    email: string
    error: 'already_accepted'
  }>
  serverError: HttpErrorType | null | undefined
  showCustomMessage: boolean
  needMorePeople: boolean
  inviting: boolean
  done: boolean
}

class Invites extends React.Component<Props, State> {
  state = {
    members: [emptyMember],
    message: '',
    invited: [],
    failed: [],
    serverError: null,
    showCustomMessage: false,
    needMorePeople: false,
    inviting: false,
    done: false,
  }

  componentDidMount() {
    if (this.props.groups.length === 0) this.props.loadGroups()
  }

  _addMember = (evt: React.SyntheticEvent<any>) => {
    evt.preventDefault()
    const newMembers = this.state.members.concat(emptyMember)
    this.setState({
      members: newMembers,
      needMorePeople: false,
    })
  }
  _handleDelete = (evt: React.SyntheticEvent<any>, index: number) => {
    evt.preventDefault()
    const newMembers = this.state.members.slice()
    newMembers.splice(index, 1)
    this.setState({
      members: newMembers,
    })
  }

  _members(): Array<Member> {
    return this.state.members
      .filter(isMemberNonEmpty)
      .filter(isMemberNotInvited(this.state.invited))
  }

  _handleInvite = (evt: React.SyntheticEvent<any>) => {
    evt.preventDefault()
    const { message } = this.state

    const actualMembers = this._members().map(member => {
      const { name, email } = getEmailHandleFromAngularBrackets(member.email)
      const emailError = !validateEmail(email)
      const updatedFields: {
        email?: string
        first_name?: string
        last_name?: string
      } = {}
      updatedFields.email = email

      if (name) {
        const [first_name, last_name] = getFirstLastName(name)
        updatedFields.first_name = first_name
        updatedFields.last_name = last_name
      }

      let territoriesError = false

      if (this.props.user.business.private_metadata.territories_enabled) {
        territoriesError = !member.territory_id
      }

      return {
        ...member,
        ...updatedFields,
        errors:
          emailError || territoriesError
            ? {
                email: emailError,
                territory_ids: territoriesError,
              }
            : null,
      }
    })

    if (actualMembers.some(member => member.errors)) {
      return this.setState({
        members: actualMembers,
        needMorePeople: false,
      })
    }

    if (actualMembers.length === 0) {
      return this.setState({
        members: [emptyMember],
        needMorePeople: true,
      })
    }

    const membersForApi = actualMembers.map(member => ({
      message: message,
      email: member.email,
      first_name: member.first_name,
      last_name: member.last_name,
      territory_ids: member.territory_id ? [member.territory_id] : undefined,
    }))
    this.setState({
      members: actualMembers,
      inviting: true,
      serverError: null,
    })
    ;(this.props.sendBusinessInvite(membersForApi) as Promise<any>)
      .then(response => {
        const { errors, invites } = response
        const done = errors.length === 0
        this.props.sendMixpanelEvent('Invite users', {
          'Users count': membersForApi.length,
        })
        let { invited, failed } = this.state
        invited = uniqBy(invited.concat(invites), 'email')
        failed = uniqBy(failed.concat(errors), 'email')
        this.setState({
          inviting: false,
          done,
          needMorePeople: false,
          failed: failed,
          invited: invited,
        })
      })
      .catch(err => {
        this.setState({
          inviting: false,
          serverError: err,
        })
      })
  }
  _handleInviteMorePeople = (evt: React.SyntheticEvent<any>) => {
    evt.preventDefault()
    this.setState({
      done: false,
      members: [emptyMember],
      invited: [],
      failed: [],
    })
  }
  _handleClose = () => {
    this.props.navigate('/settings/users')
  }
  _handleInputChange = (
    evt: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const members = updateItem(this.state.members, index, {
      ...this.state.members[index],
      [evt.target.name]: evt.target.value,
    })
    this.setState({
      members,
    })
  }
  _handleTerritoriesChange = (territory_id: number, index: number) => {
    const members = updateItem(this.state.members, index, {
      ...this.state.members[index],
      territory_id,
    })
    this.setState({
      members,
    })
  }
  _handleMessageChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      message: evt.target.value,
    })
  }
  _handleShowCustomMessage = (evt: React.SyntheticEvent<any>) => {
    evt.preventDefault()
    this.setState({
      showCustomMessage: true,
    })
  }
  _getCount = () => this._members().length
  _renderError = (
    member: Member,
    errorField?: string,
    errorMessage?: string,
  ) => {
    if (member.errors && errorField && member.errors[errorField]) {
      return <div className="Invites_Error">{errorMessage}</div>
    }
  }

  _renderValidation(member: Member) {
    if (member.errors && member.errors.email) {
      return <div className="Invites_Error">Email is required</div>
    }

    const success = this.state.invited.find(e => e.email === member.email)

    if (success) {
      return (
        <div className="Invites_Success">
          <CheckIcon
            className="Invites_SuccessIcon"
            width="1em"
            height="1em"
            color="#5f63ee"
          />
          Invite sent!
        </div>
      )
    }

    const serverError = this.state.failed.find(e => e.email === member.email)

    if (serverError) {
      if (serverError.error === 'already_accepted') {
        return (
          <div className="Invites_Success">
            Good news! {member.email} is already a member of this DISCO.
          </div>
        )
      } else {
        // as of 2017-10-14 backend doesn't throw anything
        // other than 'already_accepted', so it's only a failsafe:
        return <div className="Invites_Error">Failed to invite</div>
      }
    }
  }

  _renderTerritoryOption(member, index) {
    const { territories } = this.props
    return (
      <div className="Invites_TerritoryOption">
        {index === 0 && <Label caption="Territory" />}
        <EllipsisMenu
          compact
          caption={
            territories.find(t => t.id === member.territory_id)?.name ||
            'Select'
          }
          options={territories.map(t => ({
            label: t.name,
            value: t.id,
            type: 'checkbox',
            checked: t.id === member.territory_id ? 'all' : 'none',
          }))}
          onOptionClick={value => {
            this._handleTerritoriesChange(value, index)
          }}
        />
        {this._renderError(member, 'territory_ids', 'Select a territory')}
      </div>
    )
  }

  _renderRow = (member, index: number) => {
    const { territories_enabled } = this.props.user.business.private_metadata
    const { isPhone } = this.props
    let columnsSize = territories_enabled ? [3, 3, 3, 2, 1] : [5, 3, 3, 1]

    if (isPhone) {
      columnsSize = columnsSize.map(() => 12)
    }

    let removeButton = isPhone ? (
      <div>
        <Button
          appearance="danger"
          caption="Cancel"
          disabled={this.state.members.length === 1}
          onClick={evt => this._handleDelete(evt, index)}
          fullWidth
          textCenter
        />
      </div>
    ) : (
      <Button
        appearance="icon"
        icon="cancel"
        disabled={this.state.members.length === 1}
        onClick={evt => this._handleDelete(evt, index)}
      />
    )
    let showLabel = index === 0

    if (isPhone) {
      showLabel = isPhone
    }

    let rows = [
      <div key="1">
        <TextField
          name="email"
          label={showLabel && 'Email Address'}
          placeholder="axl@gnr.com"
          value={member.email}
          onChange={evt => this._handleInputChange(evt, index)}
        />
        {this._renderValidation(member)}
      </div>,
      <div key="2">
        <TextField
          name="first_name"
          label={showLabel && 'First name'}
          placeholder="(optional)"
          value={member.first_name}
          onChange={evt => this._handleInputChange(evt, index)}
        />
      </div>,
      <div key="3">
        <TextField
          name="last_name"
          label={showLabel && 'Last name'}
          placeholder="(optional)"
          value={member.last_name}
          onChange={evt => this._handleInputChange(evt, index)}
        />
      </div>,
    ]

    if (territories_enabled) {
      rows.push(this._renderTerritoryOption(member, index))
    }

    rows.push(
      <div>
        {showLabel && <Label caption="Action" />}
        {removeButton}
      </div>,
    )
    return (
      <Row key={index} columnsSize={columnsSize}>
        {rows}
      </Row>
    )
  }

  _renderGlobalError(): React.ReactNode {
    let message: React.ReactNode = null

    if (this.state.needMorePeople) {
      message = <span>Please add at least one email.</span>
    }

    if (this.state.serverError) {
      message = parseServerError(
        this.state.serverError?.body || this.state.serverError,
      )
    }

    if (message) {
      return (
        <Alert status="warning" icon="error">
          {message}
        </Alert>
      )
    }

    return null
  }

  _renderUserQuotaTooltip() {
    const { isTrial, usersLimits } = this.props
    return !isTrial && usersLimits.overQuota ? (
      <>
        You’re using all available seats on your <br />
        plan.{' '}
        <InlineLink to="/settings/billing/update-seats-and-tracks">
          Add more seats.
        </InlineLink>
      </>
    ) : undefined
  }

  render() {
    const { members, message, showCustomMessage, inviting } = this.state
    const { disableInviteTeamMember, bankDraftSubscriber, isPhone } = this.props

    const membersCount = this._getCount()

    let invitesDisabled, invitesDisabledDueTo
    const canManageInvitations = this.props.user?.permissions?.includes(
      'can_manage_invitations',
    )

    if (!canManageInvitations) {
      invitesDisabled = true
      invitesDisabledDueTo = (
        <div className="Invites_Warning">
          Inviting users can only be done by admins on this DISCO.
        </div>
      )
    }

    // TODO: extract `=== 'saml'` checks
    if (
      this.props.user.business.login_mechanism === 'saml' &&
      !this.props.user.is_support_user
    ) {
      invitesDisabled = true
      invitesDisabledDueTo = (
        <div className="Invites_Warning">
          Invites are disabled because SAML SSO is enabled.
        </div>
      )
    }

    const userQuotaTooltip = this._renderUserQuotaTooltip()

    if (!canManageInvitations && userQuotaTooltip) {
      return <NonAdminInviteUserModal />
    }

    if (disableInviteTeamMember) {
      return (
        <ModalWindow
          header={<Heading>Invite your team to DISCO</Heading>}
          actions={
            <ButtonContainer>
              <Button caption="Done" onClick={this._handleClose} />
            </ButtonContainer>
          }
          isOpen={true}
          onClose={this._handleClose}
          width="small"
        >
          <Text type="paragraph">
            You’ve reached your user limit. To invite more users, please{' '}
            <ContactUsLink
              className="text-link"
              intercomMsg={
                bankDraftSubscriber
                  ? 'I’d like to add more seats to my bank draft plan'
                  : 'I need to change plan to add more seats'
              }
            >
              contact us to add more seats.
            </ContactUsLink>
          </Text>
        </ModalWindow>
      )
    }

    return (
      <>
        {this.state.done ? (
          <ModalWindow
            header={<Heading>Your invitation has been sent!</Heading>}
            actions={
              <ButtonContainer>
                <Button
                  appearance="primary"
                  caption="Invite More People"
                  onClick={this._handleInviteMorePeople}
                />
                <Button caption="Done" onClick={this._handleClose} />
              </ButtonContainer>
            }
            isOpen={true}
            onClose={this._handleClose}
          />
        ) : (
          <ModalWindow
            header={<Heading>Invite your team to DISCO</Heading>}
            footer={
              !showCustomMessage && (
                <div>
                  Personalise your invite by{' '}
                  <a
                    href="#"
                    className="Invites_AddMessageBtn"
                    onClick={this._handleShowCustomMessage}
                  >
                    writing a message
                  </a>
                  .
                </div>
              )
            }
            actions={
              <Button
                appearance="primary"
                caption={
                  membersCount
                    ? `Invite ${membersCount} ${
                        membersCount === 1 ? ' Person' : ' People'
                      }`
                    : 'Invite People'
                }
                tooltip={userQuotaTooltip}
                disabled={membersCount === 0 || Boolean(userQuotaTooltip)}
                onClick={this._handleInvite}
                progressCaption="Inviting"
                progressIndicator={inviting}
              />
            }
            isOpen={true}
            onClose={this._handleClose}
          >
            <Stack space="small">
              <UsersQuotaAlert />
              {this._renderGlobalError()}
              <div />
            </Stack>

            {invitesDisabledDueTo}

            <div
              className={cn('Invites_Body', {
                disabled: invitesDisabled,
              })}
            >
              {members.map(this._renderRow)}
              <div className={isPhone ? '' : 'flc'}>
                <div>
                  <Button
                    caption="Add another member"
                    onClick={this._addMember}
                    tooltip={userQuotaTooltip}
                    disabled={Boolean(userQuotaTooltip)}
                    fullWidth={isPhone}
                    textCenter={isPhone}
                  />
                </div>
              </div>
              {showCustomMessage && (
                <Row columnsSize={[12]} marginBottom="small">
                  <div>
                    <TextField
                      multiline
                      label="Custom Message"
                      placeholder="Enter a custom message for your invitation"
                      value={message}
                      onChange={this._handleMessageChange}
                    />
                  </div>
                </Row>
              )}
            </div>
          </ModalWindow>
        )}
      </>
    )
  }
}

const ConnectedInvites = connector(Invites)

const InvitesWithNavigation = (props: any) => {
  const navigate = useNavigate()
  return <ConnectedInvites {...props} navigate={navigate} />
}

export default InvitesWithNavigation
